import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

export interface AppUpdateDialogData {
  currentVersion: string;
  latestVersion: string;
}

@Component({
  selector: 'app-update-dialog',
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="p-6 sm:p-8">
      <div class="mb-4 flex items-center gap-3">
        <mat-icon class="text-primary">update</mat-icon>
        <h2 class="text-xl font-semibold">Update Available</h2>
      </div>

      <p class="text-secondary text-sm leading-6">
        A new version of OFreight is available. Please refresh to continue with
        the latest updates and avoid working on an outdated version.
      </p>

      <div
        class="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-900/20">
        <div class="font-medium text-amber-800 dark:text-amber-200">
          If normal refresh does not work, do a hard refresh:
        </div>
        <ul
          class="mt-2 list-disc pl-5 text-amber-800/90 dark:text-amber-200/90">
          <li><strong>Windows:</strong> Ctrl + F5 or Ctrl + Shift + R</li>
          <li><strong>Mac:</strong> Cmd + Shift + R</li>
        </ul>
      </div>

      <div
        class="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-800">
        <div>
          Current version: <strong>{{ data.currentVersion }}</strong>
        </div>
        <div>
          Latest version: <strong>{{ data.latestVersion }}</strong>
        </div>
      </div>

      <div class="mt-6 flex justify-end">
        <button
          mat-flat-button
          class="rounded-md"
          color="primary"
          (click)="refreshNow()">
          Refresh now
        </button>
      </div>
    </div>
  `,
})
export class AppUpdateDialogComponent {
  data = inject<AppUpdateDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<AppUpdateDialogComponent>>(MatDialogRef);

  async refreshNow(): Promise<void> {
    this.dialogRef.close();

    // Programmatic "hard refresh": clear Cache Storage and force a fresh URL.
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(key => caches.delete(key)));
    }

    const url = new URL(window.location.href);
    url.searchParams.set('__hardRefresh', Date.now().toString());
    window.location.replace(url.toString());
  }
}
